import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { smoothScrollToTop } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const footerLinks = {
    product: [
      { name: t('layout.footer.links.features'), path: '/features' },
      { name: t('layout.footer.links.pricing'), path: '/pricing' },
      { name: t('layout.footer.links.solutions'), path: '/solutions' },
      { name: t('layout.footer.links.resources'), path: '/resources' },
    ],
    company: [
      { name: t('layout.footer.links.about'), path: '/about' },
      { name: t('layout.footer.links.blog'), path: '/blog' },
      { name: t('layout.footer.links.careers'), path: '/careers' },
      { name: t('layout.footer.links.contact'), path: '/contact' },
    ],
    resources: [
      { name: t('layout.footer.links.docs'), path: '/docs' },
      { name: t('layout.footer.links.help'), path: '/help' },
      { name: t('layout.footer.links.api'), path: '/api' },
      { name: t('layout.footer.links.status'), path: '/status' },
    ],
    legal: [
      { name: t('layout.footer.links.privacy'), path: '/privacy' },
      { name: t('layout.footer.links.terms'), path: '/terms' },
      { name: t('layout.footer.links.security'), path: '/security' },
    ],
  };

  return (
    <footer className="bg-white border-t border-slate-100 selection:bg-slate-900 selection:text-white font-sans">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-12 lg:gap-16">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-8 group">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-[10deg]">
                <span className="text-white font-semibold text-xl leading-none">S</span>
              </div>
              <span className="text-xl font-semibold text-slate-900 tracking-tighter">soma</span>
            </Link>
            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-8">
              {t('layout.footer.brand_desc')}
            </p>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              <span>{t('layout.footer.engineered_for')}</span>
              <span className="text-slate-900">{t('layout.footer.excellence')}</span>
            </div>
          </div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-[10px] font-semibold text-slate-900 uppercase tracking-[0.2em] mb-8 border-b border-slate-50 pb-4">
                {t(`layout.footer.sections.${title}`)}
              </h4>
              <ul className="space-y-4">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      onClick={() => smoothScrollToTop(500)}
                      className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-24 pt-12 border-t border-slate-50 flex flex-col md:flex-row justify-between items-center gap-8">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.2em]">
            © {currentYear} {t('layout.footer.copyright')}
          </p>

          <div className="flex items-center gap-8">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
              <span className="text-[10px] font-semibold uppercase tracking-widest">Twitter</span>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
              <span className="text-[10px] font-semibold uppercase tracking-widest">LinkedIn</span>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
              <span className="text-[10px] font-semibold uppercase tracking-widest">YouTube</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}